import { CompletionArt } from '../ui/controls';
import type { use2048Appearance } from '../../hooks/use2048Appearance';
import { type Game2048Appearance } from '../../lib/game2048Appearance';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent, RefObject } from 'react';
import type { TFunction } from 'i18next';
import { AlertCircle, ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Check, Flag, Hash, Image, Pause, Play, RotateCcw, Trophy, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DailyPuzzleGame } from '../../lib/dailyPuzzle';
import {
  GAME_2048_BOARD_SIZE,
  get2048Target,
  type Game2048Direction,
  type Game2048EngineState,
  type Game2048Outcome,
  type Game2048SpawnedTile,
  type Game2048TileMotion
} from '../../lib/game2048';
import { formatCompletionTime, formatTimeDisplay } from '../../lib/gameUtils';
import { cn, isDevelopment } from '../../lib/utils';
import { Game2048TileFace } from './Game2048TileFace';

const difficultyKeys: Record<number, string> = {
  1: 'easy',
  2: 'medium',
  3: 'hard',
  4: 'expert'
};

const gamePaths: Record<DailyPuzzleGame, string> = {
  sudoku: '/sudoku',
  '15puzzle': '/15puzzle',
  '2048': '/2048'
};

const horizontalMotionClasses: Record<number, string> = {
  [-3]: '[--tile-x:calc(-300%_-_1.5rem)] sm:[--tile-x:calc(-300%_-_2.25rem)] [--tile-y:0px]',
  [-2]: '[--tile-x:calc(-200%_-_1rem)] sm:[--tile-x:calc(-200%_-_1.5rem)] [--tile-y:0px]',
  [-1]: '[--tile-x:calc(-100%_-_0.5rem)] sm:[--tile-x:calc(-100%_-_0.75rem)] [--tile-y:0px]',
  1: '[--tile-x:calc(100%_+_0.5rem)] sm:[--tile-x:calc(100%_+_0.75rem)] [--tile-y:0px]',
  2: '[--tile-x:calc(200%_+_1rem)] sm:[--tile-x:calc(200%_+_1.5rem)] [--tile-y:0px]',
  3: '[--tile-x:calc(300%_+_1.5rem)] sm:[--tile-x:calc(300%_+_2.25rem)] [--tile-y:0px]'
};

const verticalMotionClasses: Record<number, string> = {
  [-3]: '[--tile-y:calc(-300%_-_1.5rem)] sm:[--tile-y:calc(-300%_-_2.25rem)] [--tile-x:0px]',
  [-2]: '[--tile-y:calc(-200%_-_1rem)] sm:[--tile-y:calc(-200%_-_1.5rem)] [--tile-x:0px]',
  [-1]: '[--tile-y:calc(-100%_-_0.5rem)] sm:[--tile-y:calc(-100%_-_0.75rem)] [--tile-x:0px]',
  1: '[--tile-y:calc(100%_+_0.5rem)] sm:[--tile-y:calc(100%_+_0.75rem)] [--tile-x:0px]',
  2: '[--tile-y:calc(200%_+_1rem)] sm:[--tile-y:calc(200%_+_1.5rem)] [--tile-x:0px]',
  3: '[--tile-y:calc(300%_+_1.5rem)] sm:[--tile-y:calc(300%_+_2.25rem)] [--tile-x:0px]'
};

const stationaryMotionClass = '[--tile-x:0px] [--tile-y:0px]';

const getMotionClass = (motion: Game2048TileMotion): string => {
  const rowDelta = Math.floor(motion.toIndex / GAME_2048_BOARD_SIZE) -
    Math.floor(motion.fromIndex / GAME_2048_BOARD_SIZE);
  const columnDelta = motion.toIndex % GAME_2048_BOARD_SIZE -
    motion.fromIndex % GAME_2048_BOARD_SIZE;
  if (rowDelta !== 0 && columnDelta !== 0) {
    throw new Error('Invalid diagonal 2048 tile motion');
  }
  if (columnDelta !== 0) {
    const motionClass = horizontalMotionClasses[columnDelta];
    if (!motionClass) {
      throw new Error(`Invalid horizontal 2048 tile motion: ${columnDelta}`);
    }
    return motionClass;
  }
  if (rowDelta !== 0) {
    const motionClass = verticalMotionClasses[rowDelta];
    if (!motionClass) {
      throw new Error(`Invalid vertical 2048 tile motion: ${rowDelta}`);
    }
    return motionClass;
  }
  return stationaryMotionClass;
};

export interface Game2048Animation {
  motions: Game2048TileMotion[];
  spawnedTile: Game2048SpawnedTile | null;
}

interface HeaderCardProps {
  title: string;
  target: number | null;
  score: number;
  bestScore: number;
  personalRecord: number | null;
  moves: number;
  elapsedTime: number;
  t: TFunction;
}

const HeaderCard = ({
  title,
  target,
  score,
  bestScore,
  personalRecord,
  moves,
  elapsedTime,
  t
}: HeaderCardProps) => (
  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-6 mb-4">
    <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
        <p className="text-gray-600 dark:text-gray-300">{target === null ? t('2048.topped') : t('2048.target', { target })}</p>
        {personalRecord !== null && <p className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-200">{t('2048.personalRecord', { value: personalRecord })}</p>}
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[
          [t('score'), score],
          [t('bestScore'), bestScore],
          [t('moves'), moves],
          [t('time'), formatTimeDisplay(elapsedTime)]
        ].map(([label, value]) => (
          <div key={String(label)} className="bg-gray-100 dark:bg-gray-700 rounded px-1 sm:px-3 py-2 min-w-0">
            <div className="text-xs text-gray-600 dark:text-gray-300">{label}</div>
            <div
              key={label === t('score') ? String(value) : undefined}
              className={cn(
                'font-bold tabular-nums text-gray-900 dark:text-white',
                label === t('score') && Number(value) > 0 && 'animate-score-pop motion-reduce:animate-none'
              )}
            >
              {value}
            </div>
          </div>
        ))}
      </div>
    </div>
    <ol aria-label={t('2048.progression')} className="mt-3 flex flex-wrap items-center gap-1.5">
      {[1, 2, 3, 4].map(difficulty => (
        <li
          key={difficulty}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs sm:text-sm',
            target === get2048Target(difficulty)
              ? 'bg-cobalt text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
          )}
          aria-current={target === get2048Target(difficulty) ? 'step' : undefined}
        >
          {(target === null || target > get2048Target(difficulty)) && <Check size={14} aria-label={t('2048.milestoneComplete')} />}
          {t(difficultyKeys[difficulty])} <span className="font-semibold tabular-nums">{get2048Target(difficulty)}</span>
        </li>
      ))}
    </ol>
  </div>
);

const CompletionDialog = ({ props, t }: { props: Game2048GameViewProps; t: TFunction }) => {
  if (!props.showCompletionDialog) {
    return null;
  }

  return (
    <div ref={props.completionDialogRef} role="dialog" aria-modal="true" aria-labelledby="game-2048-completion-title" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
        <CompletionArt />
        <h2 id="game-2048-completion-title" className="mb-4 break-words text-center text-base font-bold text-gray-900 dark:text-white">{props.title}</h2>
        <div className="mb-6 space-y-3 text-gray-700 dark:text-gray-300">
          <p>{t('time')}: {formatCompletionTime(props.elapsedTime)}</p>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{t('gameStatistics')}</h3>
          <ul className="list-disc pl-5">
            <li>{t('score')}: {props.engine.score}</li>
            <li>{t('moves')}: {props.engine.moves}</li>
            <li>{t('2048.tileValue', { value: props.highestTile })}</li>
          </ul>
          {props.incompleteGames.length === 0 && (
            <div className="mt-4 rounded-lg border-2 border-yellow-300 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/30">
              <p className="mb-2 text-center text-lg font-bold text-yellow-700 dark:text-yellow-300">{t('dayCompleted')} ⭐</p>
              <p className="text-center text-yellow-600 dark:text-yellow-400">{t('completedAllGames')}</p>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3">
          {props.incompleteGames.map(gameType => (
            <button key={gameType} type="button" onClick={() => { window.location.href = `${gamePaths[gameType]}/${props.seed}`; }} className="focus-ring min-h-11 rounded-lg bg-lagoon px-4 py-2 text-white hover:bg-teal-800">
              {t('2048.tryGame', { game: t(gameType) })}
            </button>
          ))}
          <button type="button" data-dialog-primary onClick={props.onCloseCompletion} className="focus-ring min-h-11 rounded-lg px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">{t('close')}</button>
        </div>
      </div>
    </div>
  );
};

interface GameOverBannerProps {
  isGameOver: boolean;
  target: number | null;
  onReset: () => void;
  t: TFunction;
}

const GameOverBanner = ({ isGameOver, target, onReset, t }: GameOverBannerProps) => {
  if (!isGameOver) {
    return null;
  }

  return (
    <div className="mb-4 flex items-start gap-3 rounded-lg border border-gray-300 bg-white p-4 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" role="alert">
      <Flag size={22} className="mt-1 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <h2 className="text-lg font-semibold">{t('2048.gameOver')}</h2>
        <p className="mt-1 max-w-prose text-sm leading-relaxed text-gray-600 dark:text-gray-300">{t('2048.gameOverDescription', { target })}</p>
        <button type="button" onClick={onReset} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed mt-3 inline-flex min-h-11 items-center gap-2 rounded-md bg-cobalt px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 focus-ring">
          <RotateCcw size={16} aria-hidden="true" />{t('restart')}
        </button>
      </div>
    </div>
  );
};

interface BoardProps {
  appearance?: Game2048Appearance;
  engine: Game2048EngineState;
  animation: Game2048Animation | null;
  isPaused: boolean;
  statusText: string;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: () => void;
  t: TFunction;
}

const Board = ({
  appearance,
  engine,
  animation,
  isPaused,
  statusText,
  onKeyDown,
  onPointerDown,
  onPointerUp,
  onPointerCancel,
  t
}: BoardProps) => {
  const movingDestinations = new Set(animation?.motions.map(motion => motion.toIndex) ?? []);
  const mergedDestinations = new Set(
    animation?.motions.filter(motion => motion.merged).map(motion => motion.toIndex) ?? []
  );

  return (
    <>
      <div
        role="grid"
        aria-label={t('2048.boardLabel')}
        aria-describedby="game-2048-status"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        className="relative isolate overflow-hidden bg-gray-400 dark:bg-gray-600 p-2 sm:p-3 rounded-lg shadow-lg max-w-xl mx-auto touch-none select-none cursor-grab active:cursor-grabbing focus-ring"
      >
        <div aria-hidden={isPaused || undefined} data-tile-layer className={cn('grid grid-cols-4 gap-2 sm:gap-3', isPaused && 'blur-2xl opacity-30')}>
          {Array.from({ length: GAME_2048_BOARD_SIZE }, (_, row) => (
            <div key={row} role="row" className="contents">
              {engine.board.slice(row * GAME_2048_BOARD_SIZE, (row + 1) * GAME_2048_BOARD_SIZE).map((tile, column) => {
                const index = row * GAME_2048_BOARD_SIZE + column;
                const motions = animation?.motions.filter(motion => motion.fromIndex === index) ?? [];
                const isSpawnedTile = animation?.spawnedTile?.index === index;
                return (
                  <div
                    key={index}
                    role="gridcell"
                    draggable={false}
                    aria-label={tile === 0 ? t('2048.emptyCell') : t('2048.tileValue', { value: tile })}
                    className="relative aspect-square rounded-md bg-gray-200 dark:bg-gray-700"
                  >
                    {tile !== 0 && <span className="sr-only">{t('2048.tileValue', { value: tile })}</span>}
                    {tile !== 0 && (
                      <div
                        data-spawned-tile={isSpawnedTile || undefined}
                        className={cn(
                          'absolute inset-0',
                          movingDestinations.has(index) && 'opacity-0 motion-reduce:opacity-100',
                          isSpawnedTile && 'animate-tile-spawn motion-reduce:animate-none',
                          mergedDestinations.has(index) && 'animate-tile-merge motion-reduce:animate-none'
                        )}
                      >
                        <Game2048TileFace value={tile} appearance={appearance} />
                      </div>
                    )}
                    {motions.map((motion, motionIndex) => (
                      <div
                        key={`${motion.fromIndex}-${motion.toIndex}-${motionIndex}`}
                        data-tile-motion={`${motion.fromIndex}-${motion.toIndex}`}
                        aria-hidden="true"
                        className={cn(
                          'absolute inset-0 z-20 animate-tile-move motion-reduce:hidden will-change-transform',
                          getMotionClass(motion)
                        )}
                      >
                        <Game2048TileFace value={motion.value} appearance={appearance} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {isPaused && (
          <div className="absolute inset-0 z-40 flex items-center justify-center rounded-lg bg-gray-950/70 backdrop-blur-md text-white">
            <div className="flex items-center gap-3 rounded-lg border border-white/20 bg-gray-950/60 px-5 py-4 text-xl font-semibold shadow-lg sm:text-2xl">
              <Pause aria-hidden="true" /> {t('gamePaused')}
            </div>
          </div>
        )}
      </div>
      <p id="game-2048-status" className="sr-only" aria-live="polite">{isPaused ? t('gamePaused') : statusText}</p>
    </>
  );
};

interface DirectionControlsProps {
  isInputLocked: boolean;
  onMove: (direction: Game2048Direction) => void;
  t: TFunction;
}

const DirectionControls = ({
  isInputLocked,
  onMove,
  t
}: DirectionControlsProps) => (
  <div className="mt-4 flex justify-center gap-2">
    <button type="button" aria-label={t('moveLeft')} disabled={isInputLocked} onClick={() => onMove('left')} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed p-3 bg-cobalt hover:bg-blue-800 text-white rounded disabled:bg-gray-400 focus-ring"><ArrowLeft /></button>
    <div className="flex flex-col gap-2">
      <button type="button" aria-label={t('moveUp')} disabled={isInputLocked} onClick={() => onMove('up')} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed p-3 bg-cobalt hover:bg-blue-800 text-white rounded disabled:bg-gray-400 focus-ring"><ArrowUp /></button>
      <button type="button" aria-label={t('moveDown')} disabled={isInputLocked} onClick={() => onMove('down')} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed p-3 bg-cobalt hover:bg-blue-800 text-white rounded disabled:bg-gray-400 focus-ring"><ArrowDown /></button>
    </div>
    <button type="button" aria-label={t('moveRight')} disabled={isInputLocked} onClick={() => onMove('right')} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed p-3 bg-cobalt hover:bg-blue-800 text-white rounded disabled:bg-gray-400 focus-ring"><ArrowRight /></button>
  </div>
);

interface PauseControlProps {
  disabled: boolean;
  isPaused: boolean;
  onToggle: () => void;
  t: TFunction;
}

const PauseControl = ({ disabled, isPaused, onToggle, t }: PauseControlProps) => (
  <button type="button" onClick={onToggle} disabled={disabled} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt hover:bg-blue-800 text-white rounded disabled:bg-gray-400 focus-ring flex items-center gap-2">
    {isPaused ? <Play size={18} /> : <Pause size={18} />}
    {isPaused ? t('resume') : t('pause')}
  </button>
);

interface UndoControlProps {
  disabled: boolean;
  onUndo: () => void;
  t: TFunction;
}

const UndoControl = ({ disabled, onUndo, t }: UndoControlProps) => (
  <button type="button" onClick={onUndo} disabled={disabled} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt hover:bg-blue-800 text-white rounded disabled:bg-gray-400 focus-ring flex items-center gap-2">
    <Undo2 size={18} /> {t('undo')}
  </button>
);

const Instructions = ({ t }: { t: TFunction }) => (
  <aside className="lg:w-80 space-y-8">
    <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">{t('howToPlay')}</h2>
      <ol className="space-y-3 text-gray-600 dark:text-gray-300 list-decimal pl-5">
        {(t('2048.instructions', { returnObjects: true }) as string[]).map(instruction => (
          <li key={instruction}>{instruction}</li>
        ))}
        <li>{t('2048.pictureInstructions')}</li>
      </ol>
      <h3 className="mt-5 text-sm font-semibold text-gray-900 dark:text-white">{t('keyboardControls')}</h3>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{t('2048.keyboardInstructions')}</p>
    </div>
  </aside>
);

interface ResetDialogProps {
  isOpen: boolean;
  dialogRef: RefObject<HTMLDivElement>;
  onClose: () => void;
  onConfirm: () => void;
  t: TFunction;
}

const ResetDialog = ({
  isOpen,
  dialogRef,
  onClose,
  onConfirm,
  t
}: ResetDialogProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="game-2048-reset-title" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 max-w-sm w-full mx-4">
        <h2 id="game-2048-reset-title" className="text-lg text-gray-900 dark:text-white mb-4">
          {t('confirmReset')}
        </h2>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 focus-ring">{t('cancel')}</button>
          <button type="button" data-dialog-primary onClick={onConfirm} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt hover:bg-blue-800 text-white rounded focus-ring">{t('confirm')}</button>
        </div>
      </div>
    </div>
  );
};

export interface Game2048GameViewProps {
  artwork?: ReturnType<typeof use2048Appearance>;
  title: string;
  engine: Game2048EngineState;
  animation: Game2048Animation | null;
  target: number | null;
  highestTile: number;
  personalRecord: number | null;
  recordMessage: string | null;
  persistenceError: string | null;
  onRetrySave: () => void;
  bestScore: number;
  elapsedTime: number;
  difficulty: number;
  seed: number;
  incompleteGames: DailyPuzzleGame[];
  isPreviewMode: boolean;
  isCompleted: boolean;
  outcome: Game2048Outcome;
  celebrateTopped: boolean;
  isInputLocked: boolean;
  isPaused: boolean;
  canUndo: boolean;
  showCompletionDialog: boolean;
  completionDialogRef: RefObject<HTMLDivElement>;
  onCloseCompletion: () => void;
  showResetDialog: boolean;
  statusText: string;
  resetDialogRef: RefObject<HTMLDivElement>;
  onMove: (direction: Game2048Direction) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: () => void;
  onTogglePause: () => void;
  onUndo: () => void;
  onReset: () => void;
  onWin: () => void;
  onCloseReset: () => void;
  onConfirmReset: () => void;
}

export const Game2048GameView = (props: Game2048GameViewProps) => {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        <section className="lg:flex-1">
          <HeaderCard
            title={props.title}
            target={props.target}
            score={props.engine.score}
            bestScore={props.bestScore}
            personalRecord={props.personalRecord}
            moves={props.engine.moves}
            elapsedTime={props.elapsedTime}
            t={t}
          />
          {props.isCompleted && props.outcome === 'active' && !props.showCompletionDialog && (
            <div role="status" className="mb-4 rounded-lg bg-blue-50 p-4 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              <p className="font-medium">{t('2048.continuePlaying')}</p>
              <p className="mt-1 text-sm">{t('2048.nextTarget')} <strong className="tabular-nums">{props.target}</strong></p>
            </div>
          )}
          {props.outcome === 'topped' && (
            <div role="status" data-topped-banner className={cn('mb-4 rounded-lg border-2 border-black bg-black p-1 text-white dark:border-white', props.celebrateTopped && 'animate-game-topped motion-reduce:animate-none')}>
              <div className="flex flex-col items-center gap-3 rounded border border-white/50 p-5 text-center sm:flex-row sm:text-left">
                <div className="shrink-0" aria-label={t('2048.tileValue', { value: 131072 })}>
                  <div className="text-5xl font-black leading-none">2<sup className="text-2xl">17</sup></div>
                  <p className="mt-2 text-sm tabular-nums text-gray-300">= 131072</p>
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-2xl font-bold">{t('2048.topped')}</h2>
                  <p className="mt-1 text-sm text-gray-300">{t('2048.toppedDescription')}</p>
                  <p className="mt-3 text-sm tabular-nums">{t('score')}: {props.engine.score} · {t('moves')}: {props.engine.moves} · {formatCompletionTime(props.elapsedTime)}</p>
                  {props.recordMessage && <p className="mt-2 text-sm font-semibold">{props.recordMessage}</p>}
                  <button type="button" onClick={props.onReset} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed mt-4 inline-flex items-center gap-2 rounded bg-white px-4 py-2 font-semibold text-black hover:bg-gray-200 focus-ring"><RotateCcw size={16} aria-hidden="true" />{t('restart')}</button>
                </div>
              </div>
            </div>
          )}
          {props.persistenceError && (
            <div role="alert" className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50 p-4 text-gray-900 dark:border-amber-800 dark:border-l-amber-500 dark:bg-amber-950/30 dark:text-white">
              <AlertCircle size={22} className="mt-1 shrink-0 text-amber-800 dark:text-amber-300" aria-hidden="true" />
              <div className="flex min-w-0 flex-1 flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold leading-snug">{t('2048.resultsNeedAttention')}</h2>
                  <p className="mt-1 max-w-prose text-sm leading-relaxed text-amber-900 dark:text-amber-100">{props.persistenceError}</p>
                </div>
                <button type="button" onClick={props.onRetrySave} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed inline-flex min-h-11 shrink-0 items-center gap-2 rounded-md bg-amber-800 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-900 focus-ring dark:bg-amber-300 dark:text-amber-950 dark:hover:bg-amber-200">
                  <RotateCcw size={16} aria-hidden="true" />{t('2048.retrySave')}
                </button>
              </div>
            </div>
          )}
          {props.recordMessage && props.outcome !== 'topped' && (
            <div role="status" className="mb-4 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
              <Trophy size={24} className="shrink-0" aria-hidden="true" />
              <h2 className="text-lg font-semibold leading-snug">{props.recordMessage}</h2>
            </div>
          )}
          <GameOverBanner
            isGameOver={props.outcome === 'lost'}
            target={props.target}
            onReset={props.onReset}
            t={t}
          />
          <ArtworkBoard
            artwork={props.artwork}
            engine={props.engine}
            animation={props.animation}
            isPaused={props.isPaused}
            statusText={props.statusText}
            onKeyDown={props.onKeyDown}
            onPointerDown={props.onPointerDown}
            onPointerUp={props.onPointerUp}
            onPointerCancel={props.onPointerCancel}
            t={t}
          />
          <ThemeReward key={props.seed} artwork={props.artwork} unlocked={props.highestTile >= 512} />
          <DirectionControls
            isInputLocked={props.isInputLocked}
            onMove={props.onMove}
            t={t}
          />
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <PauseControl
              disabled={props.outcome !== 'active' || props.showResetDialog || (props.isInputLocked && !props.isPaused)}
              isPaused={props.isPaused}
              onToggle={props.onTogglePause}
              t={t}
            />
            <UndoControl
              disabled={props.isInputLocked || !props.canUndo}
              onUndo={props.onUndo}
              t={t}
            />
            <button type="button" onClick={props.onReset} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt hover:bg-blue-800 text-white rounded focus-ring flex items-center gap-2">
              <RotateCcw size={18} /> {t('reset')}
            </button>
            {isDevelopment() && !props.isPreviewMode && (
              <button type="button" onClick={props.onWin} disabled={props.isInputLocked} title={t('2048.winHint')} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center gap-2 rounded bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400 focus-ring">
                <Trophy size={18} aria-hidden="true" /> {t('win')}
              </button>
            )}
          </div>
        </section>
        <Instructions t={t} />
      </div>
      <CompletionDialog props={props} t={t} />
      <ResetDialog
        isOpen={props.showResetDialog}
        dialogRef={props.resetDialogRef}
        onClose={props.onCloseReset}
        onConfirm={props.onConfirmReset}
        t={t}
      />
    </div>
  );
};

const ArtworkControl = ({ artwork }: { artwork: ReturnType<typeof use2048Appearance> }) => {
  const { t } = useTranslation();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const tooltipId = '2048-display-locked';

  useEffect(() => {
    if (!isTooltipOpen) return;
    const timeout = window.setTimeout(() => setIsTooltipOpen(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [isTooltipOpen]);

  useEffect(() => {
    if (!isTooltipOpen) return;
    const dismissOutside = (event: globalThis.PointerEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setIsTooltipOpen(false);
      }
    };
    document.addEventListener('pointerdown', dismissOutside, true);
    return () => document.removeEventListener('pointerdown', dismissOutside, true);
  }, [isTooltipOpen]);

  return (
    <div className="mb-4 space-y-2 text-gray-900 dark:text-white">
      <div role="radiogroup" aria-label={t('2048.tileDisplay')} aria-describedby={artwork.isLocked ? tooltipId : undefined} className="flex items-center justify-center gap-3">
        <span aria-hidden="true" className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('2048.tileDisplay')}</span>
        <div
          ref={selectorRef}
          className={cn('relative inline-flex gap-1 rounded-lg bg-gray-200 p-1 dark:bg-gray-900', artwork.isLocked && 'cursor-not-allowed')}
          onClick={() => artwork.isLocked && setIsTooltipOpen(open => !open)}
          onFocus={() => artwork.isLocked && setIsTooltipOpen(true)}
        >
          {(['numbers', 'pictures'] as const).map(mode => {
            const Icon = mode === 'numbers' ? Hash : Image;
            return (
              <label key={mode} className={cn('relative', artwork.isLocked ? 'cursor-not-allowed' : 'cursor-pointer')}>
                <input type="radio" name="2048-display" disabled={artwork.isLocked} value={mode} checked={artwork.mode === mode} onChange={() => artwork.changeMode(mode)} className="peer sr-only" />
                <span className={cn(
                  'flex min-h-11 items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold transition-colors motion-reduce:transition-none peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-indigo-600 dark:peer-focus-visible:outline-indigo-300 sm:px-4',
                  artwork.mode === mode
                    ? 'bg-white text-indigo-700 shadow-sm dark:bg-gray-700 dark:text-indigo-200'
                    : cn('text-gray-600 dark:text-gray-300', artwork.isLocked ? 'opacity-50' : 'hover:bg-gray-100 dark:hover:bg-gray-800')
                )}>
                  <Icon size={17} aria-hidden="true" />{t('2048.' + mode)}
                </span>
              </label>
            );
          })}
          {artwork.isLocked && (
            <span
              id={tooltipId}
              role="tooltip"
              className={cn(
                'pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-64 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-center text-xs font-normal text-white opacity-0 shadow-lg transition-opacity motion-reduce:transition-none dark:bg-gray-700',
                isTooltipOpen && 'opacity-100'
              )}
            >
              {t('2048.displayLocked')}
            </span>
          )}
        </div>
      </div>
      {artwork.mode === 'pictures' && (
        <div className="text-center text-sm">
          {artwork.status === 'loading' && <p role="status" className="mt-3">{t('2048.picturesLoading')}</p>}
          {artwork.status === 'error' && <div role="alert" className="mt-3">
            <p>{t('2048.picturesError')}</p>
            <button type="button" onClick={artwork.retry} className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed mt-2 rounded bg-cobalt px-4 py-2 text-white focus-ring">{t('2048.retrySave')}</button>
          </div>}
        </div>
      )}
    </div>
  );
};

const ArtworkBoard = ({ artwork, ...board }: BoardProps & { artwork?: ReturnType<typeof use2048Appearance> }) => (
  <>
    {artwork && <ArtworkControl artwork={artwork} />}
    <div className={cn(artwork?.isBlocking && 'hidden')}>
      <Board {...board} appearance={artwork?.tileAppearance} />
    </div>
  </>
);

const ThemeReward = ({ artwork, unlocked }: { artwork?: ReturnType<typeof use2048Appearance>; unlocked: boolean }) => {
  const { t } = useTranslation();
  const visible = unlocked && artwork?.mode === 'pictures';
  const announced = useRef(unlocked);
  const [announcement, setAnnouncement] = useState('');
  const themeName = artwork ? t('2048.theme.' + artwork.appearance.themeId) : '';

  useEffect(() => {
    if (!unlocked) {
      announced.current = false;
      setAnnouncement('');
    } else if (visible && !announced.current) {
      announced.current = true;
      setAnnouncement(t('2048.themeUnlocked') + ': ' + themeName);
    }
  }, [unlocked, visible, themeName, t]);

  return (
    <>
      <p className="sr-only" role="status">{announcement}</p>
      {visible && (
        <div className="mx-auto mt-3 flex max-w-xl items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/30">
          <Trophy size={24} className="shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-amber-800 dark:text-amber-200">{t('2048.themeUnlocked')}</p>
            <h2 className="text-base font-semibold leading-snug text-gray-900 dark:text-white">{themeName}</h2>
          </div>
        </div>
      )}
    </>
  );
};
